import { ReactNode, useEffect, useReducer, useRef } from "react";

import { registerMemoryStatsProvider } from "utils/memoryDiagnostics";

import { Context, initialState, reducer } from "./reducer";

// Values that are used all over the place within Explorer, but are unserializable go here instead of redux store.

export function Provider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const value = { state, dispatch };
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        const unregisterAvailableIds = registerMemoryStatsProvider("availableObjectIds", () => {
            const { availableObjectIds, forbiddenObjectIds } = stateRef.current;
            return {
                idCount: availableObjectIds?.size ?? 0,
                // Excludes the SelectionSet's lazy bitset (~maxId/8 bytes once
                // a large set answers its first `has`).
                residentBytes: availableObjectIds?.sortedIds.byteLength ?? 0,
                forbiddenIdCount: forbiddenObjectIds?.length ?? 0,
                forbiddenResidentBytes: forbiddenObjectIds?.byteLength ?? 0,
            };
        });
        const unregisterMetadataCache = registerMemoryStatsProvider("objectMetadataCache", () => {
            const stats = stateRef.current.db?.metadataCacheStats;
            return { entryCount: stats?.entryCount ?? 0, approxBytes: stats?.approxBytes ?? 0 };
        });
        const unregisterRenderView = registerMemoryStatsProvider("renderView", () => {
            const view = stateRef.current.view;
            const stats = view?.statistics?.render;
            if (!stats) {
                return { available: false };
            }
            // GPU/engine-owned; not part of heap.bytes. Caps come from the live
            // engine profile (view.deviceProfile), not Redux. Read on demand only —
            // do not subscribe this provider to the render loop.
            const bufferBytes = stats.bufferBytes ?? 0;
            const textureBytes = stats.textureBytes ?? 0;
            const gpuBytes = bufferBytes + textureBytes;
            const maxGPUBytes = view.deviceProfile?.limits.maxGPUBytes ?? 0;
            const primitives = stats.primitives ?? 0;
            const maxPrimitives = view.deviceProfile?.limits.maxPrimitives ?? 0;
            const profileExtras = view.deviceProfile as { isMobile?: boolean } | undefined;
            return {
                available: true,
                bufferBytes,
                textureBytes,
                gpuBytes,
                maxGPUBytes,
                gpuFill: maxGPUBytes > 0 ? gpuBytes / maxGPUBytes : 0,
                primitives,
                maxPrimitives,
                primitiveFill: maxPrimitives > 0 ? primitives / maxPrimitives : 0,
                triangles: stats.triangles ?? 0,
                drawCalls: stats.drawCalls ?? 0,
                detailBias: view.deviceProfile?.detailBias,
                isMobile: profileExtras?.isMobile ?? false,
            };
        });
        return () => {
            unregisterAvailableIds();
            unregisterMetadataCache();
            unregisterRenderView();
        };
    }, []);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}
